import React, { useCallback, useEffect } from 'react';

import { Box } from '@mui/material';

import { getFormAppDefinition } from '@/features/Dashboard/apps';
import type { FormAppType } from '@/features/Dashboard/apps';
import AddAppHeader from '@/features/Dashboard/drawer/components/AddAppHeader';
import { useSaveApp } from '@/features/Dashboard/drawer/components/AddAppHeader/useSaveApp';
import { DrawerFooterButtons } from '@/features/Dashboard/drawer/components/DrawerFooterButtons';
import { useDrawerSteps } from '@/features/Dashboard/drawer/context/DrawerStepsContext';
import { DrawerBody } from '@/features/Dashboard/drawer/DrawerBody';
import { FormPreview } from '@/features/Dashboard/form/AppForm/components/FormPreview';
import { useAppForm } from '@/features/Dashboard/form/AppForm/context';

const Content = ({ type }: { type: FormAppType | undefined }) => {
  if (!type) return null;
  const { Form } = getFormAppDefinition(type);
  return <Form />;
};

// Helper component to sync form dirty state to the drawer without setting state during render
const HasChangesSync = ({
  isDirty,
  setHasChanges
}: {
  isDirty: boolean;
  setHasChanges: (v: boolean) => void;
}) => {
  useEffect(() => {
    setHasChanges(isDirty);
  }, [isDirty, setHasChanges]);
  return null;
};

export const AppForm = () => {
  const { setHasChanges, handleAttemptClose } = useDrawerSteps();
  const { form } = useAppForm();
  const { handleSave, isPending } = useSaveApp();

  const createWidget = useCallback(() => {
    const values = form.state.values;
    if (!values?.type) return;
    return getFormAppDefinition(values.type as FormAppType).toWidget(values);
  }, [form]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <AddAppHeader />

      <DrawerBody>
        <form.Subscribe selector={s => s.values?.type}>
          {type => <Content type={type} />}
        </form.Subscribe>
        <Box sx={{ mt: 3 }}>
          <FormPreview />
        </Box>

        {/* Sync the drawer's hasChanges flag with the form's dirty state */}
        <form.Subscribe selector={s => s.isDirty}>
          {isDirty => (
            <HasChangesSync
              isDirty={isDirty}
              setHasChanges={setHasChanges}
            />
          )}
        </form.Subscribe>
      </DrawerBody>

      <Box sx={{ mt: 'auto' }}>
        <form.Subscribe selector={s => s.isValid}>
          {isValid => (
            <DrawerFooterButtons
              disabled={isPending || !isValid}
              onDiscard={handleAttemptClose}
              onSave={() => {
                handleSave(createWidget());
              }}
            />
          )}
        </form.Subscribe>
      </Box>
    </Box>
  );
};
